import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import urlConfig from '../config/urlConfig.js';
import StarfieldBackground from '../components/StarfieldBackground';
import CommunitySharing from '../components/CommunitySharing';
import AvatarsCarousel from '../components/AvatarsCarousel';
import BridgeOverlay from '../Popup/BridgeOverlay';
import {
  FiUsers, FiArrowRight, FiStar,
  FiBookOpen, FiMoon,
} from 'react-icons/fi';

const testimonials = [
  { text: "Andlegur is where I found my circle of belonging.", author: "Seeker from Iceland" },
  { text: "It's become my daily moment of peace, something I genuinely look forward to.", author: "Healer from Australia" },
  { text: "The rituals brought light into my evenings.", author: "Reflector from Brazil" },
  { text: "Andlegur helped me find the clarity I didn't know I was missing.", author: "Visionary from USA" },
  { text: "Finally, a sanctuary that feels alive and real.", author: "Alchemist from Canada" },
  { text: "A space that feels both grounding and transformative — exactly what I needed.", author: "Liberator from UK" },
];

export default function CommunityPage() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const nextTestimonial = () => setCurrentIndex((prev) => (prev + 1) % testimonials.length);

  return (
    <>
      <Helmet>
        {/* Primary SEO */}
        <title>Andlegur Community | Connect, Reflect &amp; Grow Together</title>
        <meta name="description" content="Join the Andlegur community — a global digital sanctuary for reflection, personal growth, and emotional well-being. Connect with members worldwide through shared practices, archetypes, and meaningful conversations." />
        <meta name="keywords" content="Andlegur community, online community for growth, reflective community, emotional support group, mindful conversations, personal development community, digital sanctuary community, shared reflection, global support network, archetype-based community, mindfulness journaling community, life alignment platform" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Andlegur" />
        <meta name="language" content="en" />
        <link rel="canonical" href={urlConfig.app.pages.community} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={urlConfig.app.pages.community} />
        <meta property="og:title" content="Andlegur Community | A Space to Connect &amp; Grow" />
        <meta property="og:description" content="Join a calm, respectful community built around reflection, shared experience, and personal growth. Andlegur connects members worldwide through archetypes, guided practices, and intentional dialogue." />
        <meta property="og:image" content={urlConfig.assets.ogImage} />
        <meta property="og:image:secure_url" content={urlConfig.assets.ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Andlegur" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={urlConfig.app.pages.community} />
        <meta name="twitter:title" content="Andlegur Community | Reflect &amp; Grow Together" />
        <meta name="twitter:description" content="Connect with others in a thoughtful digital space designed for reflection, dialogue, and growth. Discover your archetype and find your circle." />
        <meta name="twitter:image" content={urlConfig.assets.ogImage} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "OnlineCommunity",
            "name": "Andlegur Community",
            "url": urlConfig.app.pages.community,
            "description": "A supportive online community focused on reflection, emotional clarity, shared experience, and personal growth.",
            "isPartOf": {
              "@type": "Organization",
              "name": "Andlegur",
              "url": urlConfig.app.currentBaseURL,
              "sameAs": [
                "https://x.com/andlegur",
                "https://www.instagram.com/andlegur1/",
                "https://www.youtube.com/@Andlegur",
                "https://www.facebook.com/profile.php?id=61563751566301"
              ]
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "People seeking connection, reflection, and personal growth"
            }
          })}
        </script>
      </Helmet>

      <div className="relative min-h-screen overflow-x-hidden font-serif text-indigo-100">
        {/* Global starfield background */}
        <StarfieldBackground />

        {/* ① Hero — fused web 9 image */}
        <section aria-label="Andlegur community sanctuary — enter a global space for reflection and growth" className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0 pointer-events-none">
            <img
              src="/web_images/Andlegur%20web%209.webp"
              alt=""
              className="w-full h-full object-cover opacity-25 mix-blend-screen"
              loading="eager" width="1200" height="800"
            />
            <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-transparent to-[#05091B]" />
            <div className="absolute inset-0 bg-linear-to-r from-[#05091B] via-transparent to-[#05091B]" />
            <div className="absolute inset-0 bg-[#05091B]/30" />
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center py-32">
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="text-indigo-400 text-xs uppercase tracking-widest mb-5"
            >
              Trusted by people seeking clarity and personal growth
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
              className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
            >
              Your mind is full.<br /><span className="text-indigo-300">This is where you clear it.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
              className="text-indigo-200 text-lg sm:text-xl mb-10 max-w-3xl mx-auto leading-relaxed"
            >
Andlegur community is a private, judgment-free space where you can slow down, share openly, and connect with others who value growth and understanding.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <button
                onClick={() => navigate('/sanctuary/chat')}
                className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-2xl"
              >
                Enter the Space →
              </button>
              <button
                onClick={() => navigate('/register')}
                className="bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300"
              >
                View Membership
              </button>
            </motion.div>
          </div>
        </section>

        {/* ② About the Community — image left, text right */}
        <section aria-label="About the Andlegur community — a safe space for reflection and personal development" className="relative py-20 overflow-hidden">
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-2 gap-12 items-center">
              {/* Image left */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ minHeight: 420 }}>
                <img
                  src="/web_images/Andlegur%20web%2020.webp"
                  alt="Andlegur community — a safe supportive digital space for reflection, shared growth, and emotional well-being"
                  className="w-full h-full object-cover"
                  style={{ minHeight: 420 }}
                  loading="lazy" width="640" height="640"
                />
                <div className="absolute inset-0 bg-linear-to-t from-indigo-950/80 via-transparent to-transparent" />
                <div className="absolute bottom-8 left-8 right-8">
                  <p className="text-white text-2xl font-light leading-snug">"You are not alone.<br />The sanctuary holds you."</p>
                </div>
              </div>
              {/* Text right */}
              <div>
                <p className="text-indigo-400 text-xs uppercase tracking-widest mb-4">Our Community</p>
                <h2 className="text-3xl sm:text-4xl font-light text-white mb-4 leading-snug">About the Andlegur Community</h2>
                <div className="w-12 h-px bg-indigo-500 mb-6" />
                <p className="text-indigo-200 text-base leading-relaxed mb-5">
                  Andlegur community is a shared space for people on a journey of growth — connecting, reflecting, and supporting one another. A safe and supportive environment for personal development and emotional well-being.
                </p>
                <p className="text-indigo-300 text-base leading-relaxed mb-8">
                  Every member brings their own archetype, perspective, and story. Together, we form a global circle of insight, growth, and shared humanity.
                </p>
                <Link
                  to="/sanctuary"
                  className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3 rounded-full transition-all duration-300 hover:scale-105 shadow-lg"
                >
                  Explore the Sanctuary <FiArrowRight />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ③ The Circle of Members — avatar mosaic left, image right */}
        <section aria-label="Andlegur global circle of members and community archetypes" className="relative py-20 overflow-hidden">
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-2 gap-12 items-center">
              {/* Left: text + avatar mosaic */}
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <FiUsers className="w-6 h-6 text-indigo-300 shrink-0" />
                  <h2 className="text-3xl sm:text-4xl font-light text-white">The Circle of Members</h2>
                </div>
                <div className="w-12 h-px bg-indigo-500 mb-6" />
                <p className="text-indigo-200 text-base leading-relaxed mb-8">
                 The Andlegur community transforms individual reflection into shared momentum. Discover new perspectives, learn from real experiences, and stay motivated by surrounding yourself with people committed to becoming better every day.
                </p>
                <div className="grid grid-cols-6 gap-3 mb-8">
                  {Array.from({ length: 18 }).map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ scale: 0 }} whileInView={{ scale: 1 }}
                      transition={{ delay: i * 0.04 }} viewport={{ once: true }}
                      className="w-10 h-10 rounded-full bg-linear-to-br from-indigo-300 to-indigo-600 shadow-md border border-indigo-400/50"
                    />
                  ))}
                </div>
                <Link
                  to="/sanctuary/membership"
                  className="inline-flex items-center gap-2 border border-indigo-400/50 text-white hover:bg-white/5 px-7 py-3 rounded-full transition-all duration-300 text-sm"
                >
                  View Membership Options <FiArrowRight />
                </Link>
              </div>
              {/* Right: image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ minHeight: 420 }}>
                <img
                  src="/web_images/Andlegur%20web%2025.webp"
                  alt="Andlegur members — a global circle of diverse archetypes supporting each other's personal growth journey"
                  className="w-full h-full object-cover"
                  style={{ minHeight: 420 }}
                  loading="lazy" width="640" height="640"
                />
                <div className="absolute inset-0 bg-linear-to-l from-transparent to-indigo-950/30" />
              </div>
            </div>
          </div>
        </section>

        {/* ④ Avatars Carousel — archetype avatars marquee */}
        <section aria-label="Andlegur archetype avatars — choose your path and align with your purpose" className="relative py-4 overflow-hidden">
          <div className="relative z-10">
            <AvatarsCarousel />
          </div>
        </section>

        

        {/* ⑥ Guided Onboarding — 3 step cards each with top image, fused web 11 background */}
        <section aria-label="Andlegur guided onboarding — begin your reflection journey with intention" className="relative py-20 overflow-hidden">
          {/* Fused background */}
          <div className="absolute inset-0 z-0 pointer-events-none">
            <img
              src="/web_images/Andlegur%20web%2011.jpg"
              alt=""
              className="w-full h-full object-cover opacity-15 mix-blend-screen"
              loading="lazy" width="1200" height="800"
            />
            <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-transparent to-[#05091B]" />
            <div className="absolute inset-0 bg-linear-to-r from-[#05091B] via-transparent to-[#05091B]" />
          </div>
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-14">
              <p className="text-indigo-400 text-xs uppercase tracking-widest mb-3">Your Path Forward</p>
              <h2 className="text-3xl sm:text-4xl font-light text-white">Guided Onboarding</h2>
              <p className="text-indigo-300 text-base mt-4 max-w-xl mx-auto leading-relaxed">
                Begin your experience with intention. Each step invites reflection and helps personalize your journey.
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { step: '01', title: 'Reflection Prompt', desc: 'A carefully crafted question to explore your emotional state and set the tone for your path forward.', img: '/web_images/Andlegur%20web%203.webp', alt: 'Andlegur reflection prompt — guided questions for emotional self-awareness and inner clarity', Icon: FiBookOpen, color: 'border-indigo-400/50' },
                { step: '02', title: 'Ritual Timing', desc: 'Choose when your first guided practice begins — morning, evening, or a time that feels right for you.', img: '/web_images/Andlegur%20web%204.webp', alt: 'Andlegur ritual timing — personalized daily mindfulness practices and reflection sessions', Icon: FiMoon, color: 'border-violet-400/50' },
                { step: '03', title: 'Welcome Message', desc: 'Receive a personalized greeting from your chosen archetype, shaped around your unique path of becoming.', img: '/web_images/Andlegur%20web%205.webp', alt: 'Andlegur welcome message — personalized archetype greeting to begin your personal growth journey', Icon: FiStar, color: 'border-sky-400/50' },
              ].map(({ step, title, desc, img, alt, Icon, color }) => (
                <div key={step} className={`flex flex-col bg-[#0E1833]/80 backdrop-blur-sm border ${color} rounded-2xl overflow-hidden group hover:border-indigo-400/80 transition-all duration-300`}>
                  <div className="relative h-48 overflow-hidden shrink-0">
                    <img
                      src={img} alt={alt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy" width="400" height="300"
                    />
                    <div className="absolute inset-0 bg-linear-to-t from-[#0E1833] via-[#0E1833]/20 to-transparent" />
                    <span className="absolute top-4 right-4 text-white/20 text-5xl font-bold leading-none">{step}</span>
                  </div>
                  <div className="p-7 flex-1 flex flex-col">
                    <Icon className="w-5 h-5 text-indigo-300 mb-4" />
                    <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
                    <p className="text-indigo-200 text-sm leading-relaxed flex-1">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-9 py-3 rounded-full transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Get Started <FiArrowRight />
              </Link>
            </div>
          </div>
        </section>

        {/* ⑦ Membership Tiers — 3 cards each with top image */}
        <section aria-label="Andlegur membership tiers — Seeker, Bloom, and Celestial subscription plans" className="relative py-20 overflow-hidden">
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-14">
              <p className="text-indigo-400 text-xs uppercase tracking-widest mb-3">Choose Your Path</p>
              <h2 className="text-3xl sm:text-4xl font-light text-white">Membership Tiers</h2>
              <p className="text-indigo-300 text-base mt-4 max-w-xl mx-auto leading-relaxed">
                Each tier is a gateway to expanded practices, insights, and community features designed to support personal growth.
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { name: 'Seeker', aura: 'Soft Glow', tone: 'Gentle awakening', img: '/web_images/Andlegur%20web%206.webp', alt: 'Andlegur Seeker membership — foundational access to reflective practices, AI companion chat, and community', color: 'border-indigo-400/40', accent: 'text-indigo-300', benefits: ['Limited Journey Experience', 'Basic content access', 'Temporary AI companion chat', 'Community access'] },
                { name: 'Bloom', aura: 'Radiant Petal', tone: 'Emotional clarity', img: '/web_images/Andlegur%20web%207.webp', alt: 'Andlegur Bloom membership — expanded journaling prompts, archetype insights, and emotional clarity tools', color: 'border-violet-400/40', accent: 'text-violet-300', benefits: ['All Seeker benefits', 'Up to 30 journaling prompts', 'Archetype insights', 'Deeper emotional check-ins'] },
                { name: 'Celestial', aura: 'Starlit Veil', tone: 'Premium expansion', img: '/web_images/Andlegur%20web%208.webp', alt: 'Andlegur Celestial membership — premium emotional memory integration, full practice library, and guided rituals', color: 'border-sky-400/40', accent: 'text-sky-300', benefits: ['All Bloom benefits', 'Up to 60 journaling prompts', 'Emotional memory integration', 'Full practice library'] },
              ].map(({ name, aura, tone, img, alt, color, accent, benefits }) => (
                <div key={name} className={`flex flex-col bg-[#0E1833] border ${color} rounded-2xl overflow-hidden group hover:scale-[1.02] transition-all duration-300`}>
                  <div className="relative h-48 overflow-hidden shrink-0">
                    <img
                      src={img} alt={alt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy" width="400" height="300"
                    />
                    <div className="absolute inset-0 bg-linear-to-t from-[#0E1833] via-transparent to-transparent" />
                    <p className={`absolute bottom-4 left-5 text-xs uppercase tracking-widest ${accent}`}>{aura}</p>
                  </div>
                  <div className="p-7 flex-1 flex flex-col">
                    <h3 className={`text-2xl font-bold mb-1 ${accent}`}>{name}</h3>
                    <p className="text-indigo-300 text-sm italic mb-5">{tone}</p>
                    <ul className="space-y-2 mb-7 flex-1">
                      {benefits.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-indigo-200 text-sm leading-relaxed">
                          <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-indigo-400" />
                          {b}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/register"
                      className="inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-3 rounded-full transition-all duration-300 text-sm font-medium"
                    >
                      Begin Your Journey <FiArrowRight />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ⑧ Testimonials — image left, rotating quotes right */}
        <section aria-label="Community testimonials — Andlegur member experiences and reflections" className="relative py-20 overflow-hidden">
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-2 gap-12 items-center">
              {/* Image left */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ minHeight: 380 }}>
                <img
                  src="/web_images/Andlegur%20web%2024.webp"
                  alt="Andlegur community voices — member stories of reflection, emotional growth, and belonging"
                  className="w-full h-full object-cover"
                  style={{ minHeight: 380 }}
                  loading="lazy" width="640" height="500"
                />
                <div className="absolute inset-0 bg-linear-to-r from-transparent to-indigo-950/70" />
                <div className="absolute inset-0 bg-linear-to-t from-indigo-950/50 via-transparent to-transparent" />
              </div>
              {/* Testimonials right */}
              <div>
                <p className="text-indigo-400 text-xs uppercase tracking-widest mb-4">Living Voices</p>
                <h2 className="text-3xl sm:text-4xl font-light text-white mb-8">What Members Are Saying</h2>
                <div className="bg-[#0E1833]/80 backdrop-blur-sm border border-indigo-500/30 rounded-2xl p-8">
                  <FiStar className="w-5 h-5 text-indigo-300 mb-4" />
                  <p className="text-indigo-100 text-lg italic leading-relaxed mb-5">"{testimonials[currentIndex].text}"</p>
                  <p className="text-indigo-400 text-sm font-medium">— {testimonials[currentIndex].author}</p>
                  <button
                    onClick={nextTestimonial}
                    className="mt-6 text-indigo-300 hover:text-indigo-100 text-sm flex items-center gap-2 transition-colors"
                  >
                    Next Voice <FiArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ⑨ Community Sharing — complex interactive component, kept imported */}
        <CommunitySharing />

        {/* ⑩ Platform Gallery — mosaic */}
        <section aria-label="Andlegur community gallery — life inside the sanctuary" className="relative py-16 overflow-hidden">
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <p className="text-indigo-400 text-xs uppercase tracking-widest mb-3">A Closer Look</p>
              <h2 className="text-2xl sm:text-3xl font-light text-white">Life Inside Andlegur</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="col-span-2 row-span-2 relative rounded-2xl overflow-hidden shadow-2xl" style={{ minHeight: 320 }}>
                <img
                  src="/web_images/Andlegur%20web%2026.webp"
                  alt="Andlegur community space — shared reflection, meaningful conversations, and personal growth together"
                  className="w-full h-full object-cover"
                  loading="lazy" width="600" height="600"
                />
                <div className="absolute inset-0 bg-linear-to-t from-indigo-950/60 via-transparent to-transparent" />
                <p className="absolute bottom-5 left-5 text-white text-sm font-light">The Community</p>
              </div>
              {[
                { src: '/web_images/Andlegur%20web%2010.jpg', alt: 'Andlegur members connecting in the digital sanctuary for personal growth and emotional clarity' },
                { src: '/web_images/Andlegur%20web%2011.jpg', alt: 'Andlegur supportive community platform for emotional clarity, dialogue, and shared reflection' },
                { src: '/web_images/Andlegur%20web%2012.jpg', alt: 'Andlegur community features — shared experiences, group reflection, and personal development' },
                { src: '/web_images/Andlegur%20web%2014.webp', alt: 'Andlegur digital sanctuary — guided practices, archetypes, and inner growth tools for the community' },
              ].map(({ src, alt }) => (
                <div key={src} className="relative rounded-2xl overflow-hidden shadow-xl aspect-square">
                  <img src={src} alt={alt} className="w-full h-full object-cover" loading="lazy" width="400" height="400" />
                  <div className="absolute inset-0 bg-indigo-950/20" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ⑪ Final CTA — full-bleed web 12 */}
        <section aria-label="Join the Andlegur community — begin your growth and reflection journey today" className="relative overflow-hidden py-24">
          <img
            src="/web_images/Andlegur%20web%2028.webp"
            alt="Andlegur — join a global community for reflection, growth, and emotional well-being"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy" width="1200" height="600"
          />
          <div className="absolute inset-0 bg-[#0E1833]/88" />
          <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <p className="text-indigo-300 text-xs uppercase tracking-widest mb-5">Your Journey Begins Now</p>
            <h2 className="text-3xl sm:text-5xl font-light text-white mb-5 leading-snug">
              "Discover not just a page,<br />but a space of belonging."
            </h2>
            <p className="text-indigo-200 text-base sm:text-lg mb-10 leading-relaxed max-w-xl mx-auto">
              The sanctuary awaits your presence. Join a global community built around reflection, growth, and genuine human connection.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/sanctuary')}
                className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-2xl"
              >
                Discover the Space →
              </button>
              <button
                onClick={() => navigate('/get-started')}
                className="bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300"
              >
                Get the App
              </button>
            </div>
            {/* SEO internal links */}
            <div className="flex flex-wrap justify-center gap-4 mt-10 text-sm">
              {[
                { label: 'About Andlegur', to: '/about' },
                { label: 'Explore Archetypes', to: '/sanctuary/archetypes' },
                { label: 'Guided Practices', to: '/sanctuary/rituals' },
                { label: 'Membership Plans', to: '/sanctuary/membership' },
              ].map(({ label, to }) => (
                <Link key={label} to={to} className="text-indigo-300 hover:text-indigo-100 underline underline-offset-4 transition-colors">
                  {label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <BridgeOverlay />
      </div>
    </>
  );
}